import { useLogger } from '~/composables/useLogger';

/**
 * Describes a feature flag schema entry, carrying its default value and type.
 */
interface FeatureFlagSchemaEntry<T> {
	default: T;
}

/**
 * Helper to declare a feature flag default in a type-safe manner.
 * Keeps the literal type of the provided default.
 */
function defineFlag<T>(value: T): FeatureFlagSchemaEntry<T> {
	return { default: value };
}

export const FEATURE_FLAG_DEFAULTS = {
	ft_casa_de_valentina: defineFlag(false),
	ft_multi_city_cwb_core: defineFlag(false),
	ft_temp_suggest_new_tag_cwb: defineFlag(false),
	ft_temp_shorts_new_menu: defineFlag(false),
	ft_lead_flow_logs_frontend: defineFlag(false),
	ft_lead_flow_logs_backend: defineFlag(false),
	ft_pdp_shorts_cluster: defineFlag(true)
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAG_DEFAULTS;

export type FeatureFlagValue =
	| boolean
	| number
	| string
	| Record<string, unknown>
	| Array<unknown>;

/**
 * Simple object with getters for each feature flag value.
 */
type FeatureFlagValues = {
	[K in keyof typeof FEATURE_FLAG_DEFAULTS]: (typeof FEATURE_FLAG_DEFAULTS)[K]['default'];
};

/**
 * Tuple list of default flag entries [key, definition].
 */
type DefaultFlagEntries = Array<
	[FeatureFlagKey, (typeof FEATURE_FLAG_DEFAULTS)[FeatureFlagKey]]
>;

const DEFAULT_FLAG_ENTRIES = Object.entries(
	FEATURE_FLAG_DEFAULTS
) as DefaultFlagEntries;

/**
 * Returns typed values for each feature flag.
 */
export function useFeatureFlags() {
	const logger = useLogger('FeatureFlags');
	const cachedFlags = useState<FeatureFlagValues | null>(
		'featureFlagsCache',
		() => null
	);
	// Return cached object if available
	if (cachedFlags.value) {
		return cachedFlags.value;
	}

	// Values state
	const featureFlagsState = useState<
		Partial<Record<FeatureFlagKey, FeatureFlagValue>>
	>('featureFlagsState', () => ({}));

	// Build simple object with direct values
	const flags = {} as Record<FeatureFlagKey, any>;

	for (const [flagKey, definition] of DEFAULT_FLAG_ENTRIES) {
		const remoteValue = featureFlagsState.value[flagKey];
		let value = definition.default;

		if (
			remoteValue !== undefined &&
			typeof remoteValue === typeof definition.default
		) {
			value = remoteValue as typeof value;
		} else if (remoteValue !== undefined) {
			logger.warn(
				`[featureFlags] Type mismatch for flag '${String(flagKey)}'. Expected ${typeof definition.default} but got ${typeof remoteValue}. Using default value.`
			);
		}

		flags[flagKey] = value;

		if (import.meta.dev) {
			logger.info(`[featureFlags] 🆕 Set ${String(flagKey)}: ${value}`);
		}
	}

	// Cache for next calls
	cachedFlags.value = flags as FeatureFlagValues;
	return cachedFlags.value;
}
